import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { educationAPI } from "../api/axiosClient";
import { VerifyOtpRequest } from "../api/types";
import { ArrowLeft, Phone, RotateCcw } from "lucide-react";

interface OTPVerificationFormProps {
  onBack: () => void;
  onSuccess: (phoneCode: string, phoneNumber: string, otpCode: string) => void;
  phoneCode: string;
  phoneNumber: string;
  email?: string; // Email parametresi eklendi
}

const OTPVerificationForm: React.FC<OTPVerificationFormProps> = ({
  onBack,
  onSuccess,
  phoneCode,
  phoneNumber,
  email,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => {
    setError(null);
  };
  const [otpCode, setOtpCode] = useState(["", "", "", "", "", ""]);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(
        () => setResendCountdown(resendCountdown - 1),
        1000,
      );
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const validateForm = () => {
    const code = otpCode.join("");
    const newErrors: { [key: string]: string } = {};

    if (code.length !== 6) {
      newErrors.otp = "6 haneli doğrulama kodu girin";
    } else if (!/^\d{6}$/.test(code)) {
      newErrors.otp = "Sadece rakam girin";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return;

    const newCode = [...otpCode];
    newCode[index] = value;
    setOtpCode(newCode);

    // Clear OTP error when user starts typing
    if (errors.otp) {
      setErrors((prev) => ({ ...prev, otp: "" }));
    }

    if (error) {
      clearError();
    }

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      console.log("OTP verification attempt:", {
        email,
        phoneCode,
        phoneNumber,
        otpCode: otpCode.join(""),
      });

      const request: VerifyOtpRequest = {
        email: email || undefined,
        phoneCode: phoneCode,
        phoneNumber: phoneNumber,
        code: otpCode.join(""),
      };

      setLoading(true);
      setError(null);
      const response = await educationAPI.verifyOtp(request);

      if (response?.success) {
        // OTP verification successful, proceed to password reset
        onSuccess(phoneCode, phoneNumber, otpCode.join(""));
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setError(err instanceof Error ? err.message : "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    // Reset OTP input
    setOtpCode(["", "", "", "", "", ""]);
    setResendCountdown(60); // 60 seconds countdown

    // Focus first input
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    // TODO: Call resend OTP API if needed
    console.log("Resending OTP code...");
  };

  const formatPhoneNumber = (code: string, number: string) => {
    return `+${code} ${number}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Sol Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#9B51E0] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute top-40 right-20 w-48 h-48 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-32 left-20 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/5 rounded-full"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
              <Phone className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-4">SMS Doğrulama</h1>
            <p className="text-lg text-white/80">
              Telefonunuza gönderilen doğrulama kodunu girin
            </p>
          </div>

          <div className="space-y-4 mt-8">
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span>6 haneli doğrulama kodu</span>
            </div>
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span>Kod 5 dakika geçerlidir</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sağ Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#9B51E0] rounded-2xl mb-4">
              <Phone className="w-8 h-8 text-white" />
            </div>
          </div>

          <div className="mb-4 flex flex-col items-center">
            <div className="mb-6">
              <img
                src="/logo.png"
                alt="EduPayz Logo"
                className="h-20 w-auto object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Doğrulama Kodu</h2>
            <p className="text-gray-500 mt-2">
              <span className="font-medium text-[#9B51E0]">
                {formatPhoneNumber(phoneCode, phoneNumber)}
              </span>{" "}
              numarasına gönderilen kodu girin
            </p>
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  <Label className="text-sm font-medium text-gray-700 text-center block">
                    SMS Doğrulama Kodu
                  </Label>

                  <div className="flex justify-center space-x-3">
                    {otpCode.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => {
                          inputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) =>
                          handleInputChange(index, e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-12 h-14 text-center text-xl font-bold border-gray-200 focus:ring-2 focus:ring-[#9B51E0]/20 focus:border-[#9B51E0] rounded-xl"
                        disabled={loading}
                      />
                    ))}
                  </div>
                  {errors.otp && (
                    <p className="text-sm text-red-500 text-center">
                      {errors.otp}
                    </p>
                  )}
                </div>

                <div className="space-y-3">
                  <Button
                    type="submit"
                    className="w-full h-11 bg-[#9B51E0] hover:bg-[#8B41D0] text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-[#9B51E0]/25"
                    disabled={loading || otpCode.join("").length !== 6}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Doğrulanıyor...
                      </span>
                    ) : (
                      "Doğrula"
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium rounded-lg"
                    onClick={handleResendCode}
                    disabled={loading || resendCountdown > 0}
                  >
                    {resendCountdown > 0 ? (
                      <span className="flex items-center justify-center space-x-2">
                        <RotateCcw className="w-4 h-4" />
                        <span>
                          {resendCountdown} saniye sonra tekrar gönder
                        </span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center space-x-2">
                        <RotateCcw className="w-4 h-4" />
                        <span>Kodu Tekrar Gönder</span>
                      </span>
                    )}
                  </Button>

                  <button
                    type="button"
                    className="w-full flex items-center justify-center text-gray-500 hover:text-[#9B51E0] font-medium transition-colors py-2"
                    onClick={onBack}
                    disabled={loading}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Geri Dön
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-gray-400 mt-8">
            © 2026 EduPayz. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationForm;
